import React from 'react';
import PropTypes from 'prop-types';

// core
import { clearMoneyAccountBalance, getMoneyAccountBalance } from '_core/utils/api';
import { useGlobal } from '_core/hooks/useGlobal';
import { withLang } from '_core/hocs/withLang';
import { useVisitorInfo } from '_core/utils/visitorInfo';

const APP_RELOAD_GUARD_KEY = '__app_reload_guard__';

const shouldReloadOnAppNotice = () => {
  if (typeof window === 'undefined') return true;

  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(APP_RELOAD_GUARD_KEY);
    const state = raw ? JSON.parse(raw) : null;

    if (state && now - state.ts < 10000) {
      const nextCount = (state.count || 1) + 1;
      sessionStorage.setItem(
        APP_RELOAD_GUARD_KEY,
        JSON.stringify({ ts: now, count: nextCount })
      );
      return nextCount < 3;
    }

    sessionStorage.setItem(
      APP_RELOAD_GUARD_KEY,
      JSON.stringify({ ts: now, count: 1 })
    );
    return true;
  } catch (error) {
    return true;
  }
};


export const RawGlobalNotice = (props) => {
  const { getLangString: l } = props

  const {
    notify: { enqueueSnackbar },
    config: { API_NOTICE, ENABLE_APP_RELOAD_GUARD: enableAppReloadGuard = true },
  } = useGlobal()

  const visitorInfo = useVisitorInfo();
  const lastBalanceHashRef = React.useRef(null);

  const hashBalances = (list) => {
    if (!Array.isArray(list)) return '';
    // stable minimal signature of balances
    return list
      .map((acc) => `${acc.account_type}:${acc.currency}:${acc.amount?.value ?? ''}:${acc.blocked?.value ?? ''}:${acc.value?.value ?? ''}`)
      .sort()
      .join('|');
  };

  React.useEffect(() => {
    const notifyListener = (event) => {
      if (event.detail.name === API_NOTICE.APP_RELOAD) {
        if (enableAppReloadGuard && !shouldReloadOnAppNotice()) {
          return;
        }
        window.location.reload(true);
      } else if (event.detail.name === API_NOTICE.VISITOR_BALANCE) {
        const visitorId = visitorInfo.info?.id;
        if (!visitorId) {
          clearMoneyAccountBalance();
          return;
        }

        clearMoneyAccountBalance();
        getMoneyAccountBalance(visitorId)
          .then((list) => {
            const nextHash = hashBalances(list);
            const prevHash = lastBalanceHashRef.current;
            lastBalanceHashRef.current = nextHash;

            // First observed balance: cache signature, no notice.
            if (prevHash == null) {
              return;
            }

            if (prevHash != null && prevHash === nextHash) {
              return;
            }

            enqueueSnackbar(l('r._.notice.balanceupdate'), { variant: 'info' });
          })
          .catch(() => null);
      } else if (event.detail.name === API_NOTICE.CART_UPDATE) {
        enqueueSnackbar(l('r.core.good.cartimposed'), { variant: 'info' });
      } else if (event.detail.name === API_NOTICE.INSUFFICIENT_GOODS_QUANTITY) {
        enqueueSnackbar(event.detail.data, { variant: 'info' });
      }
    };
    
    window.addEventListener('notify', notifyListener);
    
    return () => window.removeEventListener('notify', notifyListener);
  }, [API_NOTICE, enqueueSnackbar, l, visitorInfo.info?.id]);

  return null;
};

RawGlobalNotice.displayName = 'RawGlobalNotice'

RawGlobalNotice.defaultProps = {}

RawGlobalNotice.propTypes = {
  // self props

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
}

export const GlobalNotice = withLang(RawGlobalNotice)
